/**
 * Porte do projeto e antecedência esperada (spec-project-form.md §7.2,
 * decisões 12 e 13 · slide 6 da apresentação da liderança).
 *
 * **Constante no front, e não tabela no banco, de propósito:** são números de
 * política da igreja, que mudam a cada anos e não a cada mês. O custo de
 * mudá-los é um deploy, não uma migration — e uma tabela traria tela de
 * cadastro, RLS e um caminho a mais para o dado ficar inconsistente.
 *
 * **O aviso que sai daqui NUNCA bloqueia** (regra 2 dos padrões da casa): ele
 * alinha expectativa e educa sobre a política, e a decisão final é humana.
 * Se algum dia alguém usar `isBelowExpectedLeadTime` para desabilitar o botão
 * de enviar, o formulário passou a mentir sobre quem decide.
 */

export interface ProjectSize {
  key: 'small' | 'medium' | 'large';
  label: string;
  /** Limite superior de público, inclusivo. `null` = sem teto. */
  maxAudience: number | null;
  /** Dias de antecedência que a política da igreja pede. */
  expectedLeadTimeDays: number;
}

export const PROJECT_SIZES: readonly ProjectSize[] = [
  { key: 'small', label: 'pequeno porte', maxAudience: 100, expectedLeadTimeDays: 30 },
  { key: 'medium', label: 'médio porte', maxAudience: 300, expectedLeadTimeDays: 45 },
  { key: 'large', label: 'grande porte', maxAudience: null, expectedLeadTimeDays: 60 },
];

/**
 * Porte a partir do público esperado. Sem público informado não há porte —
 * `null`, e não "pequeno": o campo é opcional (§7.1), e assumir o menor porte
 * faria o aviso de antecedência sumir justamente para quem não estimou nada.
 */
export function sizeForAudience(expectedAudience: number | null | undefined): ProjectSize | null {
  if (expectedAudience === null || expectedAudience === undefined) return null;
  if (!Number.isFinite(expectedAudience) || expectedAudience <= 0) return null;

  return (
    PROJECT_SIZES.find(
      (size) => size.maxAudience === null || expectedAudience <= size.maxAudience,
    ) ?? null
  );
}

/** Dias inteiros entre hoje e o início — negativo quando a data já passou. */
export function leadTimeInDays(startsAt: string | Date, now: Date = new Date()): number {
  const start = typeof startsAt === 'string' ? new Date(startsAt) : startsAt;
  if (Number.isNaN(start.getTime())) return 0;

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.floor((start.getTime() - now.getTime()) / MS_PER_DAY);
}

export interface LeadTimeWarning {
  size: ProjectSize;
  /** Quantos dias faltam para o início. */
  leadTimeDays: number;
}

/**
 * O aviso da seção 5, ou `null` quando não há o que avisar.
 *
 * Devolve dado, não frase: a mensagem em português é montada na tela, com
 * `messages.form`, porque é lá que se sabe se ela vira parágrafo ou linha de
 * destaque (RNF-U04 — banco e lógica não formatam texto).
 */
export function leadTimeWarning(
  expectedAudience: number | null | undefined,
  startsAt: string | null | undefined,
  now: Date = new Date(),
): LeadTimeWarning | null {
  if (!startsAt) return null;

  const size = sizeForAudience(expectedAudience);
  if (!size) return null;

  const leadTimeDays = leadTimeInDays(startsAt, now);
  if (leadTimeDays >= size.expectedLeadTimeDays) return null;

  return { size, leadTimeDays };
}
